import { Container, Graphics, Sprite } from 'pixi.js';
import { COLORS, WEAPON_CATEGORY_COLOR } from '@/config/colors';
import type { OfferCard } from '@/upgrades/pool';
import { getTexture } from '@/assets/resolver';
import { CARD_H, CARD_W, FACE_H, buildCardFace } from './card-face';
import { chamferPanel, hexPath, label, targetBrackets } from './widgets';

export { CARD_H, CARD_W, FACE_H } from './card-face';

/** Category tint — single source is WEAPON_CATEGORY_COLOR (config/colors). */
export const CATEGORY_COLOR: Readonly<Record<string, number>> = WEAPON_CATEGORY_COLOR;

/**
 * Offer card = the shared card face (card-face.ts) + commerce layer:
 * price plate, keep-lock padlock, OWNED state, focus brackets.
 */
export function buildOfferCard(
  oc: OfferCard,
  wallet: { yellow: number; red: number },
  selected: boolean,
  nextLevel: number,
  onToggleLock: (() => void) | null = null,
): Container {
  const afford = !oc.bought && (oc.currency === 'red' ? wallet.red : wallet.yellow) >= oc.price;
  const gemColor = oc.currency === 'red' ? COLORS.gemRed : COLORS.gemYellow;

  const c = new Container();
  // Holder tray (docs/12 N2): the card SITS in this slot — the commerce
  // band reads as part of the card's silhouette, not a floating button
  const tray = new Graphics();
  chamferPanel(tray, CARD_W + 12, CARD_H - FACE_H + 12, {
    fill: 0x0d0a16,
    stroke: selected ? COLORS.uiAccent : oc.bought ? 0x241f33 : 0x2a2440,
    strokeWidth: 2, notch: 10, plain: true,
  });
  tray.position.set(-6, FACE_H - 12);
  c.addChild(tray);

  const face = buildCardFace(oc.card, nextLevel, {
    fill: oc.bought ? 0x121019 : undefined,
    stroke: selected ? COLORS.uiAccent : undefined,
    strokeWidth: selected ? 3 : undefined,
  });
  c.addChild(face);

  // Tray contents: padlock | price
  const cy = FACE_H + (CARD_H - FACE_H) / 2 + 2;
  if (!oc.bought && onToggleLock) {
    c.addChild(buildLockButton(oc.locked, onToggleLock, 18, cy));
  }
  if (oc.bought) {
    // Owned: a big check mark — no word needed
    const tick = new Graphics();
    tick.moveTo(-12, 0).lineTo(-3, 9).lineTo(14, -10)
      .stroke({ color: COLORS.statUp, width: 5, cap: 'round', join: 'round' });
    tick.position.set(CARD_W / 2 + 14, cy);
    c.addChild(tick);
  } else {
    const cx = CARD_W / 2 + 14;
    const gem = new Sprite(getTexture(oc.currency === 'red' ? 'pickup/gem-red' : 'pickup/gem-yellow'));
    gem.anchor.set(0.5);
    gem.scale.set(0.55);
    gem.position.set(cx - 11, cy);
    const price = label(String(oc.price), 16, gemColor, 1);
    price.anchor.set(0, 0.5);
    price.position.set(cx + 5, cy);
    if (!afford) {
      gem.alpha = 0.4;
      price.alpha = 0.4;
    }
    c.addChild(gem, price);
  }

  if (oc.bought) c.alpha = 0.55;
  if (selected) {
    const br = new Graphics();
    targetBrackets(br, CARD_W, CARD_H);
    c.addChild(br);
  }
  return c;
}

/**
 * Cartoon-tech padlock toggle: locked offers survive rerolls and carry into
 * the next shop. Locked = solid yellow body with a halo ring.
 */
export function buildLockButton(locked: boolean, onToggle: () => void, x: number, y: number): Container {
  const btn = new Container();
  const color = locked ? COLORS.playerYellow : 0x5a6168;
  const g = new Graphics();
  hexPath(g, 0, 0, 16).fill(0x0f0c1a).stroke({ color, width: locked ? 3 : 2 });
  if (locked) hexPath(g, 0, 0, 19).stroke({ color, width: 1, alpha: 0.5 });
  // Shackle + body
  g.arc(0, -3, 5.5, Math.PI, 0).stroke({ color, width: 3, cap: 'round' });
  g.roundRect(-7, -2, 14, 10, 2.5).fill(locked ? color : 0x0f0c1a)
    .stroke({ color, width: 2 });
  if (locked) g.circle(0, 3, 1.8).fill(0x0f0c1a);
  btn.addChild(g);
  btn.position.set(x, y);
  btn.eventMode = 'static';
  btn.cursor = 'pointer';
  btn.on('pointertap', (e) => {
    e.stopPropagation();
    onToggle();
  });
  return btn;
}
